import { AbilityBuilder, createMongoAbility, subject } from "@casl/ability"

import type { AuthMeResponse } from "@/features/auth/model/auth-types"
import {
  WorkspaceAccessModule,
  WorkspaceAccessPermission,
} from "@/features/auth/model/auth-types"
import {
  getActiveUserBranchAccesses,
  hasWorkspaceAuthorityAccess,
} from "@/features/auth/lib/workspace-user-access"

import type { AppAbility, AppSubject } from "@/features/auth/casl/types"
import {
  BranchAdministration,
  BranchesManagement,
  RolesManagement,
  SubscriptionManagement,
  WorkspaceSettingsManagement,
} from "@/features/auth/casl/types"
import { clientPermissionGrant } from "@/features/auth/casl/permission-map"

type WorkspaceAccessRecord = AuthMeResponse["workspaceAccess"][number]

/** Empty ability: deny-by-default until rules are supplied. */
export function createEmptyAppAbility(): AppAbility {
  const { build } = new AbilityBuilder<AppAbility>(createMongoAbility)
  return build()
}

/** Every CASL subject the tenant UI knows about (module union + authority + org-governance subjects). */
const ALL_APP_SUBJECTS: readonly AppSubject[] = [
  ...Object.values(WorkspaceAccessModule),
  BranchAdministration,
  WorkspaceSettingsManagement,
  BranchesManagement,
  RolesManagement,
  SubscriptionManagement,
]

/**
 * Build tenant UI abilities.
 * - Workspace OWNER: IMPLICIT UNRESTRICTED — every action on every subject, with no branch/role/module
 *   assignment required (mirrors the backend `can(Manage, All); return`).
 * - Operational permissions come only from active custom-role assignments.
 * - Organization/parish access: WORKSPACE-scoped role assignments (`branchId === null`) only — the SYSTEM
 *   branch never confers org access; a role/UBA on it is scoped to that branch id like any other branch.
 */
export function createAppAbility(access: WorkspaceAccessRecord | null | undefined): AppAbility {
  if (!access) {
    return createEmptyAppAbility()
  }

  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)

  // Rule A — the workspace OWNER has implicit UNRESTRICTED authority: every UI gate resolves true with no
  // branch assignment, module permission, or role. AppAbility's action/subject types are closed (read/update
  // over the module union), so we grant read + update on every subject unconditionally instead of CASL's
  // `manage`/`all` wildcard (which the typed builder rejects). Mirrors the backend ability factory.
  if (hasWorkspaceAuthorityAccess(access)) {
    for (const appSubject of ALL_APP_SUBJECTS) {
      can("read", appSubject)
      can("update", appSubject)
    }
    return build()
  }

  // Custom-role permissions mirror the server ability factory. A branch-scoped assignment grants within that
  // active branch; a workspace-scoped
  // assignment (branchId null) grants workspace-wide.
  const activeRoleBranchIds = new Set(
    getActiveUserBranchAccesses(access)
      .map((uba) => uba.branch?.id)
      .filter((id): id is string => Boolean(id)),
  )
  for (const roleAssignment of access.roles ?? []) {
    // Defence in depth for a stale auth snapshot: the API omits inactive roles,
    // but no inactive role may ever become an effective CASL grant.
    if (roleAssignment.roleStatus === "INACTIVE") continue
    const branchId = roleAssignment.branchId
    if (branchId && !activeRoleBranchIds.has(branchId)) continue
    for (const permission of roleAssignment.permissions ?? []) {
      const grant = clientPermissionGrant(permission)
      if (!grant) continue
      for (const action of grant.actions) {
        if (branchId) {
          can(action, grant.subject, { branchId } as never)
        } else {
          can(action, grant.subject)
        }
      }

      // MEMBERS edit/manage carries group and ministry management on the same scope. This mirrors the
      // backend tenant-authorization service and keeps the role-based model behavior identical on both sides.
      if (permission === "members.edit" || permission === "members.manage") {
        for (const inheritedSubject of [WorkspaceAccessModule.GROUPS, WorkspaceAccessModule.MINISTRIES]) {
          if (branchId) {
            can("read", inheritedSubject, { branchId } as never)
            can("update", inheritedSubject, { branchId } as never)
          } else {
            can("read", inheritedSubject)
            can("update", inheritedSubject)
          }
        }
      }
    }
  }

  // Department access roles (additive, mirrors the server ability factory): each grants its bundle scoped to BOTH
  // the branch AND the department, so a department grant matches only that department's records (never branch-wide).
  // Self-contained: NOT gated on active branch access.
  for (const departmentRole of access.departmentAccess ?? []) {
    const { branchId, departmentId } = departmentRole
    if (!branchId || !departmentId) continue
    for (const permission of departmentRole.permissions ?? []) {
      const grant = clientPermissionGrant(permission)
      if (!grant) continue
      for (const action of grant.actions) {
        can(action, grant.subject, { branchId, departmentId } as never)
      }
    }
  }

  return build()
}

/** CASL check for a module scoped to a specific department (uses `subject` + `{ branchId, departmentId }`). */
export function appAbilityAllowsModuleForDepartment(
  ability: AppAbility,
  module: WorkspaceAccessModule,
  requiredAccess: WorkspaceAccessPermission,
  branchId: string | null | undefined,
  departmentId: string | null | undefined,
): boolean {
  if (!branchId || !departmentId) return false
  const scoped = subject(module, { branchId, departmentId }) as never
  if (requiredAccess === WorkspaceAccessPermission.EDIT) {
    return ability.can("update", scoped)
  }
  return ability.can("read", scoped)
}

/** CASL check for a module in a specific branch scope (uses `subject` + `branchId` conditions). */
export function appAbilityAllowsModuleForBranch(
  ability: AppAbility,
  module: WorkspaceAccessModule,
  requiredAccess: WorkspaceAccessPermission,
  branchId: string | null | undefined,
): boolean {
  if (module === WorkspaceAccessModule.SETTINGS) {
    const canReadScoped = branchId
      ? ability.can("read", subject(WorkspaceAccessModule.SETTINGS, { branchId }) as never)
      : false
    const canUpdateScoped = branchId
      ? ability.can("update", subject(WorkspaceAccessModule.SETTINGS, { branchId }) as never)
      : false
    if (requiredAccess === WorkspaceAccessPermission.EDIT) {
      return ability.can("update", WorkspaceAccessModule.SETTINGS) || canUpdateScoped
    }
    return ability.can("read", WorkspaceAccessModule.SETTINGS) || canReadScoped
  }

  if (!branchId) return false
  const scoped = subject(module, { branchId }) as never
  if (requiredAccess === WorkspaceAccessPermission.EDIT) {
    return ability.can("update", scoped)
  }
  return ability.can("read", scoped)
}

/**
 * CASL check for a module at WORKSPACE (organization / parish) scope — an UNSCOPED grant not tied to any
 * branch. It matches only rules with no branch condition: a workspace-scoped role assignment (`branchId ===
 * null`) or the OWNER's unrestricted grant. A branch-scoped grant — INCLUDING one on the SYSTEM branch — does
 * NOT satisfy it. We check against an instance carrying no `branchId`, so MongoQuery `{ branchId }` rules fail
 * to match while unconditional rules match. This is how "parish"/organization UI access is resolved (Rule B).
 */
export function appAbilityAllowsModuleUnscoped(
  ability: AppAbility,
  module: WorkspaceAccessModule,
  requiredAccess: WorkspaceAccessPermission,
): boolean {
  const unscoped = subject(module, {}) as never
  if (requiredAccess === WorkspaceAccessPermission.EDIT) {
    return ability.can("update", unscoped)
  }
  return ability.can("read", unscoped)
}

/** Branch-administration authority for a specific branch, granted by assigned roles. */
export function appAbilityAllowsBranchAdministration(
  ability: AppAbility,
  branchId: string | null | undefined,
  requiredAccess: WorkspaceAccessPermission = WorkspaceAccessPermission.VIEW,
): boolean {
  if (!branchId) return false
  const scoped = subject(BranchAdministration, { branchId }) as never
  if (requiredAccess === WorkspaceAccessPermission.EDIT) {
    return ability.can("update", scoped)
  }
  return ability.can("read", scoped)
}

/** Explicit mono-workspace settings authority, scoped to the one active branch. */
export function appAbilityAllowsWorkspaceSettingsManagement(
  ability: AppAbility,
  branchId: string | null | undefined,
  requiredAccess: WorkspaceAccessPermission = WorkspaceAccessPermission.VIEW,
): boolean {
  if (!branchId) return false
  const scoped = subject(WorkspaceSettingsManagement, { branchId }) as never
  return requiredAccess === WorkspaceAccessPermission.EDIT
    ? ability.can("update", scoped)
    : ability.can("read", scoped)
}

/** Workspace administration is an unscoped workspace-level ability, not a branch/module fallback. */
export function appAbilityAllowsWorkspaceSettings(
  ability: AppAbility,
  requiredAccess: WorkspaceAccessPermission = WorkspaceAccessPermission.VIEW,
): boolean {
  if (requiredAccess === WorkspaceAccessPermission.EDIT) {
    return ability.can("update", WorkspaceAccessModule.SETTINGS)
  }

  return ability.can("read", WorkspaceAccessModule.SETTINGS)
}
